/**
 * @file indexedDbSync.ts
 *
 * @description
 * indexedDBに保存されたローカルデータを外部システム（S3, API）と同期するためのビジネスロジックを提供
 *
 * 主な責務
 * - indexedDB内のデータの取得、更新、削除を実行
 * - APIへのデータ送信（同期・アップロード）
 * - データ整合性の保持（ex: 状態管理`sendState`の更新）
 * - エラーのthrow
 */

import * as indexedDB from '@/accessors/indexedDB/common/indexedDBController'
import { isOnlineByHealthChecker } from '@/modules/healthCheck/healthChecker'
import type {
  IDBContainer,
  IDBInspectionResult,
  ResQueryInspectionResult
} from '@/types/accessor/api/emergencyInspectionResult/emergencyInspectionResult'
import { upsertEmergencyInspectionResultApi } from '@/accessors/api/requests/disasterEmergencyInspectionResultApiGwFuncs'
import photoDBOperator from '@/accessors/indexedDB/photoDBOperator'
import { OFFLINE_UPLOAD_RESULT_CODE } from '@/constants/modules/offlineDataUpload/resultCode'
import { STORE_NAME as INSPECTION_RESULT_IDB_STORE_NAME } from '@/accessors/indexedDB/disasterEmergencyInspectionResultIdb'
import { METHOD, SEND_STATE } from '@/constants/accessor/indexedDB/indexedDB'
import MiiaFwConfig from '@/configs/config'
import { GlobalError } from '@/utils/globalError/GlobalError'
import { ERROR_CODES } from '@/constants/error/errorCode'

/**
 * 調査実施結果のステータスを更新する
 * @param uploadSuccess アップロード結果 true:成功 false:失敗
 * @param assessmentId 判定ID
 * @returns なし
 */
const setUpdateInspectionState = async (
  uploadSuccess: boolean,
  assessmentId: string
): Promise<void> => {
  const sendState = uploadSuccess ? 'uploadSuccess' : 'uploadFailed'
  await indexedDB.updateSendState(INSPECTION_RESULT_IDB_STORE_NAME, sendState, assessmentId)
}

/**
 * 調査実施結果のIDBを更新する
 * @param assessmentId 判定ID
 * @param data 更新するデータ
 * @returns なし
 */
const updateInspectionResultIDB = async (
  assessmentId: string,
  data: IDBInspectionResult
): Promise<void> => {
  await indexedDB.set(
    INSPECTION_RESULT_IDB_STORE_NAME,
    data,
    METHOD.UPDATE,
    'uploadSuccess',
    assessmentId
  )
}

/**
 * 調査実施結果APIの更新リクエストを実行する
 * @param reqData 調査実施結果API(upsert)リクエストデータ群
 * @returns なし
 */
const executeInspectionDataUpload = async (reqData: IDBInspectionResult): Promise<void> => {
  try {
    const apiPram = {
      system: MiiaFwConfig.config.sysId,
      govCode: reqData.govCode,
      needRecords: true,
      disasterId: reqData.disasterId,
      inspectionResultList: [
        {
          assessmentId: reqData.assessmentId,
          userId: reqData.insertId,
          emergencyAssessment: reqData.emergencyAssessment,
          assessmentInfo: reqData.assessmentInfo,
          building: reqData.building,
          buildingOverview: reqData.buildingOverview,
          assessmentDetail: reqData.assessmentDetail
        }
      ]
    }
    // needRecords=trueなので、必ずqueryのレスポンスが返ってくる
    const res = (await upsertEmergencyInspectionResultApi(apiPram)) as ResQueryInspectionResult
    for (const data of res.resultList) {
      if (data.assessmentId != null) {
        const reqData: IDBInspectionResult = {
          ...data,
          disasterId: apiPram.disasterId,
          govCode: apiPram.govCode
        }
        await updateInspectionResultIDB(data.assessmentId, reqData)
      }
    }
  } catch {
    throw new GlobalError([ERROR_CODES.RESULT_SAVE_FAILED])
  }
}

/**
 * オフラインデータの一括アップデート処理の呼び出し
 * @returns 終了コード 0: 正常 1: 異常 2: 警告
 */
export const executeDataUpdate = async (): Promise<OFFLINE_UPLOAD_RESULT_CODE> => {
  let resCode = OFFLINE_UPLOAD_RESULT_CODE.SUCCESS
  let errorCnt = 0
  try {
    // 回線のヘルスチェック
    if (!(await isOnlineByHealthChecker())) {
      return (resCode = OFFLINE_UPLOAD_RESULT_CODE.NETWORK_ERROR)
    }
    window.localStorage.setItem('dataUploadInProgress', '0')
    const dataKeys = await indexedDB.getAllKeys(INSPECTION_RESULT_IDB_STORE_NAME)

    await Promise.allSettled(
      dataKeys.map(async key => {
        const inspectionResultIdb = (await indexedDB.get(
          INSPECTION_RESULT_IDB_STORE_NAME,
          key
        )) as IDBContainer
        // 処理対象外のものはスキップ
        if (inspectionResultIdb === undefined) return
        if (inspectionResultIdb.send_state !== SEND_STATE.EDITED) return
        if (
          inspectionResultIdb.data == undefined ||
          (inspectionResultIdb.data as IDBInspectionResult).assessmentId == undefined
        ) {
          // 不正なデータの可能性があるためアップロード対象としない
          await setUpdateInspectionState(false, key)
          throw new Error(
            `assessmentId:${key}の関連データに不備があったため処理をスキップしました。`
          )
        }
        const reqData = inspectionResultIdb.data as IDBInspectionResult
        // 写真のアップロード結果はログのみ出力し、IndexDBには反映しない。
        await photoDBOperator
          .executeS3Upload(reqData.govCode, reqData.disasterId, reqData.assessmentId)
          .catch(e => {
            throw e
          })

        // 調査完了済の調査結果アップロード
        await executeInspectionDataUpload(reqData)
      })
    ).then(result => {
      for (const r of result) {
        if (r.status === 'rejected') {
          console.error(r.reason)
          errorCnt++
        }
      }
    })
  } catch (e) {
    console.error(e)
    // IndexDBに正常にアクセスできない場合など
    resCode = OFFLINE_UPLOAD_RESULT_CODE.UNEXPECTED_ERROR
  } finally {
    window.localStorage.removeItem('dataUploadInProgress')
  }
  if (errorCnt > 0) resCode = OFFLINE_UPLOAD_RESULT_CODE.INCOMPLETE_PROCESSING_ERROR
  return resCode
}
